class Quantity {
    input: HTMLInputElement;
    #increase: HTMLElement | null;
    #decrease: HTMLElement | null;
    #cart: HTMLButtonElement | null;

    min: number;
    max: number;
    #stock: number;
    #isUpdating: boolean = false;
    #limitMessageTimeout: number | null = null;

    // Shared event object to save memory
    static readonly CHANGE_EVENT = new Event('change', {bubbles: true});

    constructor(private element: HTMLElement) {
        if (this.element.dataset.initialized === 'true') return;

        this.input = element.querySelector<HTMLInputElement>('input[type="number"]')!;

        if (!this.input || element.dataset.initialized) return;

        this.#increase = element.querySelector<HTMLElement>('.increase-quantity');
        this.#decrease = element.querySelector<HTMLElement>('.decrease-quantity');
        this.#cart = element.closest('form')?.querySelector<HTMLButtonElement>('button[name="update_cart"]') || null;

        // Sync initial state
        this.min = this.#getNumericAttr(this.input, 'min', 1);
        this.max = this.#getNumericAttr(this.input, 'max', 9999);
        this.#stock = this.#getNumericAttr(element, 'data-stock-quantity', 0);

        this.#init();
        element.dataset.initialized = 'true';
    }

    #getNumericAttr(el: HTMLElement, attr: string, fallback: number): number {
        const val = parseInt(el.getAttribute(attr) || '', 10);
        return isNaN(val) ? fallback : val;
    }

    #init() {
        this.#increase?.addEventListener('click', () => this.updateValue(1));
        this.#decrease?.addEventListener('click', () => this.updateValue(-1));

        this.input.addEventListener('change', this.handleInputChange);

        // Sync initial UI
        this.toggleStockWarning(this.currentValue);

        window.addEventListener('woocommerce_variation_stock_quantity_changed', this.handleGlobalStockUpdate);
        window.addEventListener('woocommerce_cart_updated', this.handleGlobalStockUpdate);
    }

    private get currentValue(): number {
        return parseInt(this.input.value, 10) || 0;
    }

    private handleGlobalStockUpdate = () => {
        this.#stock = this.#getNumericAttr(this.element, 'data-stock-quantity', 0);
        this.toggleStockWarning(this.currentValue);
    };

    private updateValue(delta: number) {
        const nextValue = this.currentValue + delta;

        if (nextValue >= this.min && nextValue <= this.max) {
            this.input.value = nextValue.toString();
            this.input.dispatchEvent(Quantity.CHANGE_EVENT);
            this.#cart?.removeAttribute('disabled');
        } else if (nextValue < this.min) {
            this.showLimitMessage(`Minimaal ${this.min} vereist.`);
        }
    }

    private handleInputChange = () => {
        if (this.#isUpdating) return;

        const val = this.currentValue;

        if (val < this.min) {
            this.#isUpdating = true;
            this.input.value = this.min.toString();
            this.input.setAttribute('aria-invalid', 'true');
            this.showLimitMessage(`Minimaal ${this.min} vereist.`);
            this.#isUpdating = false;
        } else {
            this.input.removeAttribute('aria-invalid');
            this.element.toggleAttribute('data-at-min', val <= this.min);
        }

        this.toggleStockWarning(this.input.valueAsNumber || val);
    };

    private showLimitMessage(html: string) {
        if (this.#limitMessageTimeout) window.clearTimeout(this.#limitMessageTimeout);

        let msgEl = this.element.querySelector('.quantity-limit-message');
        if (!msgEl) {
            msgEl = document.createElement('div');
            msgEl.className = 'quantity-limit-message';
            this.element.appendChild(msgEl);
        }

        msgEl.textContent = html;

        this.#limitMessageTimeout = window.setTimeout(() => {
            msgEl?.remove();
            this.#limitMessageTimeout = null;
        }, 2000);
    }

    private toggleStockWarning(value: number) {
        const parent = this.element.parentElement;
        if (!parent) return;

        const existingWarning = parent.querySelector('.quantity-stock-message');

        if (this.#stock > 0 && value > this.#stock) {
            const warningHtml = `<i class="fa-regular fa-circle-exclamation"></i> Nog ${this.#stock} op voorraad, overige ${value - this.#stock} leverbaar op bestelling.`;

            if (existingWarning) {
                if (existingWarning.innerHTML !== warningHtml) existingWarning.innerHTML = warningHtml;
            } else {
                const el = document.createElement('div');
                el.className = 'quantity-stock-message';
                el.innerHTML = warningHtml;
                parent.appendChild(el);
            }
        } else {
            existingWarning?.remove();
        }
    }
}

const initQuantities = () => {
    document.querySelectorAll<HTMLElement>('.woocommerce .quantity')?.forEach(el => new Quantity(el));
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initQuantities);
} else {
    initQuantities();
}

window.addEventListener('woocommerce_cart_updated', initQuantities);